<template>
  <div class="clients-shell">

    <!-- Toast Notifications -->
    <transition-group name="toast" tag="div" class="toast-container">
      <div v-for="toast in toasts" :key="toast.id" :class="['toast-item', `toast--${toast.type}`]" @click="removeToast(toast.id)">
        <v-icon small class="toast-icon">{{ toast.icon }}</v-icon>
        <span class="toast-message">{{ toast.message }}</span>
      </div>
    </transition-group>

    <!-- Confetti Canvas -->
    <canvas v-show="showConfetti" ref="confettiCanvas" class="confetti-canvas"></canvas>

    <!-- Agent ribbon (from API) -->
    <div class="agent-ribbon" v-if="agentTopUpSerial">
      <div class="ribbon-inner">
        <div class="ribbon-left">
          <span class="ribbon-label">TopUp Agent</span>
          <span class="ribbon-name">{{ agentName || 'Agent' }}</span>
        </div>
        <div class="ribbon-right">
          <div class="serial-pill" :title="agentTopUpSerial">
            <span class="pill-prefix">AGT</span>
            <span class="pill-mask">{{ maskedTopUpSerial }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="agent-ribbon ribbon-error" v-else-if="agentError">
      <div class="ribbon-inner">
        <v-icon small color="#EF4444">mdi-alert-circle</v-icon>
        <span class="ribbon-err">{{ agentError }}</span>
      </div>
    </div>
    <div class="agent-ribbon ribbon-loading" v-else-if="agentLoading">
      <div class="ribbon-inner">
        <span class="load-dots"></span>
        <span class="ribbon-load">Verifying agent credentials…</span>
      </div>
    </div>

    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <div class="header-icon">
          <v-icon color="white" small>mdi-account-group</v-icon>
        </div>
        <div>
          <h1 class="page-title">My Clients</h1>
          <p class="page-sub">Users enrolled under your agent link</p>
        </div>
      </div>
      <div class="header-right">
        <div class="stat-pill">
          <span class="stat-num">{{ filteredClients.length }}</span>
          <span class="stat-lbl">{{ filteredClients.length === 1 ? 'client' : 'clients' }}</span>
        </div>
      </div>
    </div>

    <!-- Share Section (améliorée) -->
    <div class="referral-share">
      <div class="referral-header">
        <div class="referral-label">
          <v-icon x-small class="mr-1">mdi-link-variant</v-icon>
          Your Enrollment Link
        </div>
        <div class="referral-reward">
          <v-icon x-small color="#42A5F5">mdi-gift</v-icon>
          <span>Earn commissions</span>
        </div>
      </div>

      <div class="referral-link-preview" @click="copyReferralLink" title="Click to copy">
        <span class="referral-link-text">{{ truncatedReferralLink }}</span>
        <button class="copy-inline-btn" @click.stop="copyReferralLink" title="Copy Link">
          <v-icon x-small color="white">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
        </button>
      </div>

      <div class="share-buttons-row">
        <button v-for="btn in shareButtons" :key="btn.name" class="share-pill" :class="'share-pill--' + btn.type" @click="btn.action" :title="btn.label">
          <i :class="btn.icon"></i>
          <span class="pill-label">{{ btn.label }}</span>
        </button>
      </div>
    </div>

    <!-- Toolbar: search + filter -->
    <div class="toolbar-row">
      <div class="search-wrap">
        <v-icon small color="#94A3B8" class="search-icon">mdi-magnify</v-icon>
        <input
          v-model="search"
          class="search-input"
          placeholder="Search by name, username or phone…"
        />
        <button v-if="search" class="search-clear" @click="search = ''">
          <v-icon x-small color="#94A3B8">mdi-close</v-icon>
        </button>
      </div>
      <div class="filter-tabs">
        <button
          v-for="tab in filters"
          :key="tab.key"
          class="filter-tab"
          :class="{ active: activeFilter === tab.key }"
          @click="activeFilter = tab.key"
        >
          <v-icon x-small left :color="activeFilter === tab.key ? 'white' : '#64748B'">{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-block">
      <div class="loader-ring" />
      <span>Loading your clients…</span>
    </div>

    <!-- Empty -->
    <div v-else-if="filteredClients.length === 0" class="state-block">
      <div class="empty-icon">
        <v-icon size="48" color="#CBD5E1">mdi-account-off-outline</v-icon>
      </div>
      <p class="empty-title">No clients yet</p>
      <p class="empty-sub">Share your enrollment link to start adding clients</p>
      <button class="enroll-btn-large" @click="copyReferralLink">
        <v-icon small left color="white">mdi-content-copy</v-icon>
        Copy Enrollment Link
      </button>
    </div>

    <!-- Table (desktop) -->
    <div v-else class="table-wrap">
      <table class="clients-table">
        <thead>
          <tr>
            <th class="th-check">
              <input type="checkbox" v-model="selectAll" class="custom-cb" />
            </th>
            <th @click="sortBy('fullnames')" class="sortable">
              Client
              <v-icon x-small :color="sort.key === 'fullnames' ? '#00479B' : '#CBD5E1'">
                {{ sort.key === 'fullnames' && sort.dir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th>Phone</th>
            <th>Email</th>
            <th @click="sortBy('date')" class="sortable">
              Joined
              <v-icon x-small :color="sort.key === 'date' ? '#00479B' : '#CBD5E1'">
                {{ sort.key === 'date' && sort.dir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th>Status</th>
            <th>Balance</th>
            <th class="th-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(client, idx) in paginatedClients"
            :key="client.id"
            class="client-row"
            :class="{ 'row-selected': selectedIds.includes(client.id) }"
            :style="{ animationDelay: idx * 0.04 + 's' }"
          >
            <td class="td-check">
              <input type="checkbox" :value="client.id" v-model="selectedIds" class="custom-cb" />
            </td>

            <!-- Client info -->
            <td class="td-client">
              <div class="client-info">
                <div class="avatar" :style="{ background: avatarColor(client.username) }">
                  {{ initials(client.fullnames || client.username) }}
                </div>
                <div class="client-meta">
                  <div class="client-name">{{ client.fullnames || '—' }}</div>
                  <div class="client-username">@{{ client.username }}</div>
                </div>
              </div>
            </td>

            <td class="td-phone">
              <a :href="'tel:' + client.phone_number" class="phone-link">
                {{ client.phone_number || '—' }}
              </a>
            </td>

            <td class="td-email">
              <span class="email-text">{{ client.email || '—' }}</span>
            </td>

            <td class="td-date">
              <span class="date-text">{{ formatDate(client.time_created) }}</span>
            </td>

            <td class="td-status">
              <span class="status-chip" :class="statusClass(client)">
                <span class="status-dot" />
                {{ statusLabel(client) }}
              </span>
            </td>

            <td class="td-balance">
              <span class="balance-text">{{ formatBalance(client.balance) }}</span>
            </td>

            <td class="td-actions">
              <div class="action-btns">
                <button class="action-btn btn-view" @click="viewClient(client)" title="View profile">
                  <v-icon x-small color="#00479B">mdi-eye</v-icon>
                </button>
                <button class="action-btn btn-msg" @click="messageClient(client)" title="Message">
                  <v-icon x-small color="#009688">mdi-message-text</v-icon>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination-row">
        <span class="pagination-info">
          Showing {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, sortedClients.length) }}
          of {{ sortedClients.length }}
        </span>
        <div class="pagination-btns">
          <button
            class="pg-btn"
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            <v-icon small>mdi-chevron-left</v-icon>
          </button>
          <button
            v-for="p in totalPages"
            :key="p"
            class="pg-btn pg-num"
            :class="{ active: p === currentPage }"
            @click="currentPage = p"
          >{{ p }}</button>
          <button
            class="pg-btn"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            <v-icon small>mdi-chevron-right</v-icon>
          </button>
        </div>
        <div class="per-page-wrap">
          <select v-model="perPage" class="per-page-select">
            <option :value="10">10 / page</option>
            <option :value="25">25 / page</option>
            <option :value="50">50 / page</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Client detail drawer -->
    <div class="detail-drawer" :class="{ open: drawerOpen }">
      <div class="drawer-overlay" @click="drawerOpen = false" />
      <div class="drawer-panel" v-if="selectedClient">
        <div class="drawer-header">
          <div class="drawer-avatar" :style="{ background: avatarColor(selectedClient.username) }">
            {{ initials(selectedClient.fullnames || selectedClient.username) }}
          </div>
          <div class="drawer-title-block">
            <h3 class="drawer-name">{{ selectedClient.fullnames || '—' }}</h3>
            <span class="drawer-username">@{{ selectedClient.username }}</span>
          </div>
          <button class="drawer-close" @click="drawerOpen = false">
            <v-icon small color="#64748B">mdi-close</v-icon>
          </button>
        </div>

        <div class="drawer-body">
          <div class="drawer-section">
            <div class="ds-label">Contact</div>
            <div class="drawer-row">
              <v-icon x-small color="#64748B">mdi-phone</v-icon>
              <span>{{ selectedClient.phone_number || '—' }}</span>
            </div>
            <div class="drawer-row">
              <v-icon x-small color="#64748B">mdi-email</v-icon>
              <span>{{ selectedClient.email || '—' }}</span>
            </div>
          </div>

          <div class="drawer-section">
            <div class="ds-label">Account</div>
            <div class="drawer-row">
              <v-icon x-small color="#64748B">mdi-calendar</v-icon>
              <span>Joined {{ formatDate(selectedClient.time_created) }}</span>
            </div>
            <div class="drawer-row">
              <v-icon x-small color="#64748B">mdi-identifier</v-icon>
              <span>{{ selectedClient.serial_no || '—' }}</span>
            </div>
            <div class="drawer-row">
              <v-icon x-small color="#64748B">mdi-wallet</v-icon>
              <span>{{ formatBalance(selectedClient.balance) }}</span>
            </div>
          </div>

          <div class="drawer-section">
            <div class="ds-label">Verification</div>
            <div class="drawer-row">
              <v-icon x-small :color="selectedClient.isVerified ? '#10B981' : '#EF4444'">
                {{ selectedClient.isVerified ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              <span>{{ selectedClient.isVerified ? 'Email verified' : 'Not verified' }}</span>
            </div>
            <div class="drawer-row">
              <v-icon x-small :color="selectedClient.kyc_status === 'approved' ? '#10B981' : '#F97316'">
                mdi-shield-account
              </v-icon>
              <span>KYC: {{ selectedClient.kyc_status }}</span>
            </div>
          </div>
        </div>

        <div class="drawer-footer">
          <router-link
            :to="{ name: 'savings.clients.detail', params: { id: selectedClient.id } }"
            class="drawer-action-btn btn-primary"
          >
            View Full Profile
          </router-link>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TopUpClients',

  data() {
    return {
      clients: [],
      loading: false,

      // Agent TopUp info (via API)
      agentTopUpSerial: '',
      agentName: '',
      agentLoading: false,
      agentError: '',

      // Toasts & confetti
      toasts: [],
      toastId: 0,
      showConfetti: false,
      copied: false,

      search: '',
      activeFilter: 'all',
      filters: [
        { key: 'all',      label: 'All',      icon: 'mdi-account-group' },
        { key: 'verified', label: 'Verified',  icon: 'mdi-check-circle' },
        { key: 'pending',  label: 'Pending',   icon: 'mdi-clock-outline' },
      ],

      sort: { key: 'date', dir: 'desc' },

      currentPage: 1,
      perPage: 10,

      selectedIds: [],
      selectAll: false,

      drawerOpen: false,
      selectedClient: null,
    }
  },

  computed: {
    // Agent serial from API (pas du store)
    agentSerialForLink() {
      return this.agentTopUpSerial
    },

    encryptedReferralCode() {
      if (!this.agentSerialForLink) return ''
      try {
        const payload = `pc_ref:${this.agentSerialForLink}`
        return btoa(payload)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '')
      } catch (e) {
        console.error('Encode error:', e)
        return ''
      }
    },

    generateReferralLink() {
      const baseUrl = "https://myapp.pettycashglobal.com"
      // Lien d'enrollment TopUp avec code encrypté (comme dans le formulaire d'inscription)
      return `${baseUrl}/register/topup/${this.agentSerialForLink}?ref=${this.encryptedReferralCode}`
    },

    truncatedReferralLink() {
      const link = this.generateReferralLink
      return link.length > 45 ? link.substring(0, 45) + '...' : link
    },

    canShareNative() {
      return typeof navigator !== 'undefined' && !!navigator.share
    },

    shareButtons() {
      const buttons = [
        { name: 'copy', type: 'copy', icon: 'fas fa-copy', label: 'Copy', action: () => this.copyReferralLink() },
        { name: 'whatsapp', type: 'whatsapp', icon: 'fab fa-whatsapp', label: 'WhatsApp', action: () => this.shareViaWhatsApp() },
        { name: 'facebook', type: 'facebook', icon: 'fab fa-facebook-f', label: 'Facebook', action: () => this.shareViaFacebook() },
        { name: 'sms', type: 'sms', icon: 'fas fa-sms', label: 'SMS', action: () => this.shareViaSMS() },
      ]
      if (this.canShareNative) {
        buttons.push({ name: 'more', type: 'more', icon: 'fas fa-share-alt', label: 'More', action: () => this.shareNative() })
      }
      return buttons
    },

    maskedTopUpSerial() {
      const raw = this.agentTopUpSerial
      if (!raw) return '···· ····'
      const m = raw.match(/^([A-Za-z]+)(\d+)$/)
      if (!m) return raw
      const prefix = m[1]
      const nums = m[2]
      const len = nums.length
      if (len <= 4) return prefix + ' ·' + '·'.repeat(len - 2) + nums.slice(-2)
      const visible = nums.slice(-3)
      const maskedCount = Math.max(4, len - 3)
      return prefix + ' ' + '·'.repeat(maskedCount) + visible
    },

    filteredClients() {
      let list = [...this.clients]

      if (this.search.trim()) {
        const q = this.search.toLowerCase()
        list = list.filter(c =>
          (c.fullnames || '').toLowerCase().includes(q) ||
          (c.username  || '').toLowerCase().includes(q) ||
          (c.phone_number || '').includes(q) ||
          (c.email || '').toLowerCase().includes(q)
        )
      }

      if (this.activeFilter === 'verified') list = list.filter(c => c.isVerified)
      if (this.activeFilter === 'pending')  list = list.filter(c => !c.isVerified)

      return list
    },

    sortedClients() {
      return [...this.filteredClients].sort((a, b) => {
        let valA, valB
        if (this.sort.key === 'date') {
          valA = new Date(a.time_created)
          valB = new Date(b.time_created)
        } else {
          valA = (a.fullnames || a.username || '').toLowerCase()
          valB = (b.fullnames || b.username || '').toLowerCase()
        }
        if (valA < valB) return this.sort.dir === 'asc' ? -1 : 1
        if (valA > valB) return this.sort.dir === 'asc' ? 1 : -1
        return 0
      })
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.sortedClients.length / this.perPage))
    },

    paginatedClients() {
      const start = (this.currentPage - 1) * this.perPage
      return this.sortedClients.slice(start, start + this.perPage)
    },
  },

  watch: {
    selectAll(val) {
      this.selectedIds = val ? this.paginatedClients.map(c => c.id) : []
    },
    search() { this.currentPage = 1 },
    activeFilter() { this.currentPage = 1 },
    perPage() { this.currentPage = 1 },
  },

  async mounted() {
    document.title = 'PettyCash | My Clients'
    await this.loadAgentInfo()   // Récupère le serial TopUp
    if (this.agentTopUpSerial) {
      await this.fetchClients()
    } else {
      this.loading = false
      this.showToast('Unable to load agent information. Please contact support.', 'error', 'mdi-alert')
    }
  },

  methods: {
    async loadAgentInfo() {
      this.agentLoading = true
      this.agentError = ''
      try {
        const res = await axios.get('/api/v1/topup/my-agent-info/')
        if (res.data && res.data.serial_no) {
          this.agentTopUpSerial = res.data.serial_no
          this.agentName = res.data.full_name || res.data.username
        } else {
          this.agentError = "Vous n'êtes pas autorisé à gérer des clients TopUp."
        }
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.agentError = 'Accès refusé : vous devez être un agent TopUp.'
        } else {
          this.agentError = 'Impossible de récupérer vos informations. Vérifiez votre connexion.'
        }
        console.error(error)
      } finally {
        this.agentLoading = false
      }
    },

    async fetchClients() {
      this.loading = true
      try {
        const res = await axios.get('/api/v1/topup/clients/')
        this.clients = res.data
      } catch (err) {
        this.showToast('Failed to load clients. Please try again.', 'error', 'mdi-alert')
      } finally {
        this.loading = false
      }
    },

    sortBy(key) {
      if (this.sort.key === key) {
        this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.key = key
        this.sort.dir = 'asc'
      }
    },

    initials(name) {
      if (!name) return '?'
      return name.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    },

    avatarColor(username) {
      const colors = [
        'linear-gradient(135deg,#00479B,#0066CC)',
        'linear-gradient(135deg,#009688,#4DB6AC)',
        'linear-gradient(135deg,#F97316,#FB923C)',
        'linear-gradient(135deg,#8B5CF6,#A78BFA)',
        'linear-gradient(135deg,#10B981,#34D399)',
        'linear-gradient(135deg,#D4AF37,#F59E0B)',
      ]
      let hash = 0
      for (const c of (username || '')) hash = c.charCodeAt(0) + ((hash << 5) - hash)
      return colors[Math.abs(hash) % colors.length]
    },

    formatDate(dt) {
      if (!dt) return '—'
      return new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    formatBalance(bal) {
      if (bal == null) return '—'
      return new Intl.NumberFormat('fr-CM', { style: 'currency', currency: 'XAF', maximumFractionDigits: 0 }).format(bal)
    },

    statusLabel(client) {
      if (client.isVerified && client.kyc_status === 'approved') return 'Active'
      if (client.isVerified) return 'Verified'
      return 'Pending'
    },

    statusClass(client) {
      if (client.isVerified && client.kyc_status === 'approved') return 'status-active'
      if (client.isVerified) return 'status-verified'
      return 'status-pending'
    },

    viewClient(client) {
      this.selectedClient = client
      this.drawerOpen = true
    },

    messageClient(client) {
      this.showToast(`Opening chat with ${client.username}…`, 'info', 'mdi-message-text')
    },

    // ========== LOGIQUE DE PARTAGE COMPLÈTE ==========
    showToast(message, type = 'success', icon = 'mdi-check-circle') {
      const id = ++this.toastId
      this.toasts.push({ id, message, type, icon })
      setTimeout(() => this.removeToast(id), 4000)
    },

    removeToast(id) {
      this.toasts = this.toasts.filter(t => t.id !== id)
    },

    triggerConfetti() {
      this.showConfetti = true
      const canvas = this.$refs.confettiCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight

      const particles = []
      const colors = ['#1565C0', '#FFD700', '#FF9800', '#42A5F5', '#9C27B0', '#00C853']

      for (let i = 0; i < 100; i++) {
        particles.push({
          x: canvas.width / 2,
          y: canvas.height / 2,
          vx: (Math.random() - 0.5) * 20,
          vy: (Math.random() - 0.5) * 20 - 5,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 8 + 4,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 10,
          opacity: 1,
        })
      }

      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        let active = false

        particles.forEach(p => {
          if (p.opacity > 0) {
            active = true
            p.x += p.vx
            p.y += p.vy
            p.vy += 0.3
            p.rotation += p.rotationSpeed
            p.opacity -= 0.015

            ctx.save()
            ctx.translate(p.x, p.y)
            ctx.rotate((p.rotation * Math.PI) / 180)
            ctx.globalAlpha = p.opacity
            ctx.fillStyle = p.color
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size)
            ctx.restore()
          }
        })

        if (active) {
          requestAnimationFrame(animate)
        } else {
          this.showConfetti = false
        }
      }

      requestAnimationFrame(animate)
    },

    copyReferralLink() {
      const link = this.generateReferralLink
      navigator.clipboard.writeText(link).then(() => {
        this.copied = true
        this.showToast('Enrollment link copied to clipboard!', 'success', 'mdi-content-copy')
        this.triggerConfetti()
        setTimeout(() => { this.copied = false }, 2000)
      }).catch(() => {
        this.showToast('Failed to copy link', 'error', 'mdi-alert')
      })
    },

    shareViaWhatsApp() {
      const link = this.generateReferralLink
      const serial = this.agentTopUpSerial
      const message = `🌟 Join PettyCash Business Suite!\n\nUse my enrollment link:\n${link}\n\nReferral code: ${serial}\n\n_PettyCash — Fintech Made in Cameroon_ 🇨🇲`
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank')
      this.showToast('Opening WhatsApp...', 'info', 'mdi-whatsapp')
    },

    shareViaFacebook() {
      const link = this.generateReferralLink
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
        '_blank',
        'width=600,height=400'
      )
      this.showToast('Opening Facebook...', 'info', 'mdi-facebook')
    },

    shareViaSMS() {
      const link = this.generateReferralLink
      const serial = this.agentTopUpSerial
      const body = `Join PettyCash with my code ${serial}: ${link}`
      window.open(`sms:?body=${encodeURIComponent(body)}`)
      this.showToast('Opening SMS...', 'info', 'mdi-message')
    },

    async shareNative() {
      if (!navigator.share) return
      const link = this.generateReferralLink
      const serial = this.agentTopUpSerial
      try {
        await navigator.share({
          title: 'PettyCash Business Suite',
          text: `Join PettyCash with my referral code ${serial}`,
          url: link,
        })
        this.showToast('Shared successfully!', 'success', 'mdi-share-variant')
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Share failed:', e)
      }
    },
  },
}
</script>

<style scoped>
/* ── TOKENS ── */
.clients-shell {
  --c-primary:  #00479B;
  --c-teal:     #009688;
  --c-green:    #10B981;
  --c-gold:     #D4AF37;
  --c-ink:      #0F172A;
  --c-muted:    #64748B;
  --c-border:   #E2E8F0;
  --c-surface:  #F8FAFC;

  min-height: 100vh;
  background: #F0F4FA;
  padding: 1.5rem;
  position: relative;
  font-family: 'DM Sans', 'Inter', system-ui, sans-serif;
}

/* Toast & Confetti */
.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.toast-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  cursor: pointer;
  min-width: 280px;
  animation: toastSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid;
}
.toast--success { border-left-color: #00C853; }
.toast--error { border-left-color: #FF5252; }
.toast--info { border-left-color: #2196F3; }
.toast--warning { border-left-color: #FF9800; }
.toast-message { font-size: 14px; font-weight: 500; color: #333; }
.toast-enter-active, .toast-leave-active { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter, .toast-leave-to { opacity: 0; transform: translateX(100%); }

.confetti-canvas {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
  z-index: 9998;
}

@keyframes toastSlideIn {
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
}

/* Agent Ribbon (comme dans le formulaire d'inscription) */
.agent-ribbon {
  background: #003D82;
  color: white;
  padding: 0.55rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  margin-bottom: 1rem;
  border-radius: 12px;
}
.ribbon-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.ribbon-left { display: flex; align-items: baseline; gap: 0.6rem; }
.ribbon-label { font-size: 0.7rem; opacity: 0.65; font-weight: 500; text-transform: uppercase; letter-spacing: 0.06em; }
.ribbon-name { font-size: 0.85rem; font-weight: 600; }

.serial-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  cursor: default;
}
.pill-prefix { color: #D4AF37; font-weight: 700; }
.pill-mask { color: rgba(255,255,255,0.85); font-weight: 500; }

.ribbon-error { background: #7F1D1D; }
.ribbon-err { font-size: 0.8rem; font-weight: 500; }
.ribbon-loading { background: #1E293B; }
.ribbon-load { font-size: 0.8rem; opacity: 0.8; }

.load-dots {
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #059669;
  position: relative;
  animation: dotBounce 1.4s ease-in-out infinite;
}
.load-dots::before, .load-dots::after {
  content: '';
  position: absolute;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #059669;
  animation: dotBounce 1.4s ease-in-out infinite;
}
.load-dots::before { left: -10px; animation-delay: -0.32s; }
.load-dots::after { left: 10px; animation-delay: -0.16s; }
@keyframes dotBounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* Header, Referral Share, Toolbar, Table, Drawer (inchangés / identiques à la version précédente) */
.page-header {
  position: relative; z-index: 1;
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap; gap: 1rem;
}
.header-left { display: flex; align-items: center; gap: 0.9rem; }
.header-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: linear-gradient(135deg, var(--c-primary), #0066CC);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(0,71,155,0.3);
}
.page-title { font-size: 1.4rem; font-weight: 800; color: var(--c-ink); letter-spacing: -0.03em; margin: 0; }
.page-sub   { font-size: 0.8rem; color: var(--c-muted); margin: 0; }

.header-right { display: flex; align-items: center; gap: 0.75rem; }
.stat-pill {
  display: flex; align-items: baseline; gap: 0.3rem;
  background: white; border: 1px solid var(--c-border);
  padding: 0.4rem 0.8rem; border-radius: 999px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.stat-num { font-size: 1.1rem; font-weight: 800; color: var(--c-primary); }
.stat-lbl { font-size: 0.75rem; color: var(--c-muted); font-weight: 600; }

.referral-share {
  background: white;
  border: 1px solid var(--c-border);
  padding: 14px 18px;
  border-radius: 18px;
  max-width: 620px;
  margin: 0 0 1.25rem 0;
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
}
.referral-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}
.referral-label {
  font-size: 10px;
  color: var(--c-muted);
  display: flex;
  align-items: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 600;
}
.referral-reward {
  font-size: 10px;
  color: #FF8F00;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}
.referral-link-preview {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 10px;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
}
.referral-link-preview:hover {
  background: #F1F5F9;
  border-color: var(--c-primary);
}
.referral-link-text {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #1e293b;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.3px;
}
.copy-inline-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  transition: all 0.2s;
  border-radius: 6px;
}
.copy-inline-btn:hover { opacity: 1; background: rgba(0,71,155,0.1); }
.share-buttons-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-start;
}
.share-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  letter-spacing: 0.3px;
  outline: none;
  position: relative;
  overflow: hidden;
}
.share-pill::before {
  content: '';
  position: absolute;
  top: 0; left: -100%;
  width: 100%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}
.share-pill:hover::before { left: 100%; }
.share-pill i { font-size: 14px; }
.share-pill:hover { transform: translateY(-3px); filter: brightness(1.05); }
.share-pill:active { transform: translateY(0) scale(0.96); }
.share-pill--copy {
  background: #EFF6FF;
  color: #00479B;
  border: 1px solid #BFDBFE;
}
.share-pill--whatsapp {
  background: #25D366;
  color: white;
  box-shadow: 0 2px 8px rgba(37,211,102,0.3);
}
.share-pill--facebook {
  background: #1877F2;
  color: white;
  box-shadow: 0 2px 8px rgba(24,119,242,0.3);
}
.share-pill--sms {
  background: #FF9800;
  color: white;
  box-shadow: 0 2px 8px rgba(255,152,0,0.3);
}
.share-pill--more {
  background: #F1F5F9;
  color: #334155;
  border: 1px solid #E2E8F0;
}
@media (max-width: 599px) {
  .share-pill .pill-label { display: none; }
}

.toolbar-row {
  position: relative; z-index: 1;
  display: flex; align-items: center; gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.search-wrap {
  flex: 1; min-width: 220px;
  display: flex; align-items: center;
  background: white; border: 1.5px solid var(--c-border);
  border-radius: 10px; padding: 0 0.75rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.search-wrap:focus-within {
  border-color: rgba(0,71,155,0.4);
  box-shadow: 0 0 0 3px rgba(0,71,155,0.07);
}
.search-icon { flex-shrink: 0; opacity: 0.6; }
.search-input {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 0.875rem; color: var(--c-ink);
  padding: 0.65rem 0.5rem; font-weight: 500;
}
.search-input::placeholder { color: #B0BDCF; }
.search-clear { background: none; border: none; cursor: pointer; padding: 0; opacity: 0.6; }
.filter-tabs { display: flex; gap: 0.4rem; }
.filter-tab {
  display: flex; align-items: center; gap: 0.3rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px; border: 1.5px solid var(--c-border);
  background: white; font-size: 0.78rem; font-weight: 600;
  color: var(--c-muted); cursor: pointer;
  transition: all 0.2s;
}
.filter-tab.active {
  background: var(--c-primary); border-color: var(--c-primary);
  color: white; box-shadow: 0 2px 8px rgba(0,71,155,0.25);
}
.filter-tab:not(.active):hover { border-color: #CBD5E1; background: #F8FAFC; }

.state-block {
  position: relative; z-index: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 4rem 2rem; gap: 1rem;
  background: white; border-radius: 16px;
  border: 1px solid var(--c-border);
  color: var(--c-muted); font-size: 0.9rem;
}
.loader-ring {
  width: 36px; height: 36px;
  border: 3px solid #E2E8F0;
  border-top-color: var(--c-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.empty-icon { opacity: 0.5; }
.empty-title { font-size: 1.1rem; font-weight: 700; color: var(--c-ink); margin: 0; }
.empty-sub   { font-size: 0.875rem; color: var(--c-muted); margin: 0; }
.enroll-btn-large {
  margin-top: 0.5rem;
  display: flex; align-items: center; gap: 0.4rem;
  padding: 0.65rem 1.25rem;
  border-radius: 10px; border: none;
  background: linear-gradient(135deg, var(--c-primary), #0066CC);
  color: white; font-size: 0.875rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 12px rgba(0,71,155,0.3);
  transition: transform 0.2s;
}
.enroll-btn-large:hover { transform: translateY(-2px); }

.table-wrap {
  position: relative; z-index: 1;
  background: white; border-radius: 16px;
  border: 1px solid var(--c-border);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.clients-table {
  width: 100%; border-collapse: collapse;
  font-size: 0.875rem;
}
.clients-table thead tr { border-bottom: 1.5px solid var(--c-border); }
.clients-table th {
  padding: 0.9rem 1rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--c-muted);
  white-space: nowrap;
  background: #FAFBFC;
}
.clients-table th.sortable { cursor: pointer; user-select: none; }
.clients-table th.sortable:hover { color: var(--c-primary); }
.th-check, .td-check { width: 40px; padding-left: 1rem !important; }
.th-actions { text-align: right; padding-right: 1.25rem !important; }
.client-row {
  border-bottom: 1px solid #F1F5F9;
  transition: background 0.15s;
  animation: rowIn 0.4s ease both;
}
@keyframes rowIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
}
.client-row:hover { background: #F8FAFC; }
.client-row.row-selected { background: rgba(0,71,155,0.03); }
.client-row:last-child { border-bottom: none; }
.clients-table td {
  padding: 0.85rem 1rem;
  vertical-align: middle;
}
.custom-cb {
  width: 16px; height: 16px;
  accent-color: var(--c-primary);
  cursor: pointer;
}
.client-info { display: flex; align-items: center; gap: 0.75rem; }
.avatar {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 0.72rem; font-weight: 700;
  flex-shrink: 0;
}
.client-name     { font-weight: 600; color: var(--c-ink); font-size: 0.875rem; white-space: nowrap; }
.client-username { font-size: 0.75rem; color: var(--c-muted); }
.phone-link  { color: var(--c-teal); font-weight: 500; text-decoration: none; white-space: nowrap; }
.phone-link:hover { text-decoration: underline; }
.email-text  { color: var(--c-muted); font-size: 0.82rem; white-space: nowrap; }
.date-text   { color: var(--c-muted); font-size: 0.82rem; white-space: nowrap; }
.balance-text { font-weight: 700; color: var(--c-ink); white-space: nowrap; }
.status-chip {
  display: inline-flex; align-items: center; gap: 0.35rem;
  padding: 0.25rem 0.65rem; border-radius: 999px;
  font-size: 0.72rem; font-weight: 700;
}
.status-dot { width: 6px; height: 6px; border-radius: 50%; }
.status-active   { background: rgba(16,185,129,0.1);  color: #059669; }
.status-active   .status-dot { background: #10B981; }
.status-verified { background: rgba(0,71,155,0.08);   color: #00479B; }
.status-verified .status-dot { background: #00479B; }
.status-pending  { background: rgba(249,115,22,0.1);  color: #EA580C; }
.status-pending  .status-dot { background: #F97316; }
.td-actions { text-align: right; }
.action-btns { display: flex; justify-content: flex-end; gap: 0.4rem; }
.action-btn {
  width: 30px; height: 30px; border-radius: 8px; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s;
}
.btn-view { background: rgba(0,71,155,0.08); }
.btn-view:hover { background: rgba(0,71,155,0.16); }
.btn-msg  { background: rgba(0,150,136,0.08); }
.btn-msg:hover  { background: rgba(0,150,136,0.16); }
.pagination-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid var(--c-border);
  flex-wrap: wrap; gap: 0.75rem;
}
.pagination-info { font-size: 0.78rem; color: var(--c-muted); }
.pagination-btns { display: flex; gap: 0.3rem; }
.pg-btn {
  min-width: 32px; height: 32px; border-radius: 8px;
  border: 1.5px solid var(--c-border);
  background: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.8rem; font-weight: 600; color: var(--c-muted);
  transition: all 0.15s;
}
.pg-btn:hover:not(:disabled) { border-color: var(--c-primary); color: var(--c-primary); }
.pg-btn.active { background: var(--c-primary); border-color: var(--c-primary); color: white; }
.pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.per-page-select {
  padding: 0.3rem 0.5rem;
  border-radius: 8px; border: 1.5px solid var(--c-border);
  font-size: 0.78rem; font-weight: 600; color: var(--c-muted);
  background: white; cursor: pointer; outline: none;
}
.detail-drawer { position: fixed; inset: 0; z-index: 100; pointer-events: none; }
.detail-drawer.open { pointer-events: all; }
.drawer-overlay {
  position: absolute; inset: 0;
  background: rgba(15,23,42,0.35); backdrop-filter: blur(2px);
  opacity: 0; transition: opacity 0.3s;
}
.detail-drawer.open .drawer-overlay { opacity: 1; }
.drawer-panel {
  position: absolute; top: 0; right: 0; bottom: 0;
  width: 340px; background: white;
  box-shadow: -8px 0 32px rgba(0,0,0,0.12);
  display: flex; flex-direction: column;
  transform: translateX(100%); transition: transform 0.35s cubic-bezier(0.16,1,0.3,1);
}
.detail-drawer.open .drawer-panel { transform: translateX(0); }
.drawer-header {
  display: flex; align-items: center; gap: 0.9rem;
  padding: 1.25rem;
  border-bottom: 1px solid var(--c-border);
}
.drawer-avatar {
  width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: white; font-weight: 700; font-size: 0.85rem;
  flex-shrink: 0;
}
.drawer-title-block { flex: 1; }
.drawer-name { font-size: 1rem; font-weight: 700; color: var(--c-ink); margin: 0; }
.drawer-username { font-size: 0.78rem; color: var(--c-muted); }
.drawer-close { background: none; border: none; cursor: pointer; padding: 0.25rem; opacity: 0.7; }
.drawer-body { flex: 1; overflow-y: auto; padding: 1rem 1.25rem; }
.drawer-section { margin-bottom: 1.25rem; }
.ds-label {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: var(--c-muted);
  margin-bottom: 0.6rem;
}
.drawer-row {
  display: flex; align-items: center; gap: 0.6rem;
  font-size: 0.85rem; color: var(--c-ink);
  padding: 0.4rem 0;
  border-bottom: 1px solid #F1F5F9;
}
.drawer-row:last-child { border-bottom: none; }
.drawer-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--c-border);
}
.drawer-action-btn {
  display: block; width: 100%;
  padding: 0.75rem; border-radius: 10px;
  text-align: center; font-weight: 700; font-size: 0.875rem;
  text-decoration: none; transition: all 0.2s;
}
.btn-primary {
  background: linear-gradient(135deg, var(--c-primary), #0066CC);
  color: white;
  box-shadow: 0 3px 10px rgba(0,71,155,0.3);
}
.btn-primary:hover { transform: translateY(-1px); box-shadow: 0 5px 16px rgba(0,71,155,0.4); }

@media (max-width: 768px) {
  .clients-shell { padding: 1rem; }
  .clients-table th:nth-child(4),
  .clients-table td:nth-child(4),
  .clients-table th:nth-child(7),
  .clients-table td:nth-child(7) { display: none; }
  .drawer-panel { width: 100%; }
  .filter-tabs .filter-tab span { display: none; }
}
</style>
